import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import "../css/home.css";
import "../css/homeempty.css";
import "../css/homeresp.css";
import { dailySeries, trendOf, TileBars, HERO } from "../search/SearchHero";
import startArt from "../assets/start-art.webp";
import { loadInfo } from "../search/mapInfo";
import useCompanies from "../search/useCompanies";
import useAllOrders from "../search/useAllOrders";
import { notExpired, cityOf, ttlActive, DEFAULT_LOAD_TTL_HOURS } from "../loads/common";
import { fetchMatchSettings } from "../../utils/matchingApi";
import HomeLoadResults from "../search/HomeLoadResults";
import { filterLoads } from "../loads/filterLoads";
import "../search/search.css";
import { Icon, D, VehiclePhoto } from "../search/icons";
import CompanyLogo from "../search/CompanyLogo";
import { Modal, Field } from "../search/ui";
import MapPanel from "../search/MapPanel";
import { truckInfo } from "../search/mapInfo";
import { TruckResultsDialog } from "../search/dialogs";
import SearchFields from "../search/SearchFields";
import TruckRequestPanel from "../search/TruckRequestPanel";
import { TruckDetailsPanel } from "../ui/cards";
import RowMenu, { useHiddenRows, useRowMenu } from "../ui/RowMenu";
import { useSentRequests } from "../search/store";
import {
  EMPTY_FILTERS,
  countWord,
  destCities,
  filterTrucks,
  fmtAgo,
  fmtFree,
  knownCities,
  loadRecent,
  nf,
  popularRoutes,
  pushRecent,
  sortTrucks,
  describeFilters,
  truckText,
  useLiveTrucks,
  SEARCH_FORBIDDEN_TEXT,
  useMetrics,
} from "../search/liveTrucks";

const PAGE = 50;
const rowKey = (t) => `${t.driver_id}-${t.vehicle_id}`;

export default function HomePage() {
  const navigate = useNavigate();
  const { trucks, loading, error, forbidden, reload } = useLiveTrucks();
  const role = useSelector((s) => s.auth.user?.role);
  const userId = useSelector((s) => s.auth.user?.id);
  const rowMenu = useRowMenu();
  const hid = useHiddenRows("trucks", userId);
  const hidLoads = useHiddenRows("loads", userId); // same list as «Поиск» → «Найти грузы»
  const [loadTtl, setLoadTtl] = useState(DEFAULT_LOAD_TTL_HOURS);
  useEffect(() => {
    let live = true;
    fetchMatchSettings()
      .then((s) => live && setLoadTtl(Number(s?.loadTtlHours) || DEFAULT_LOAD_TTL_HOURS))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  const metrics = useMetrics(role);
  const sent = useSentRequests();
  const { byOwner, byName, all: companies } = useCompanies();
  const allOrders = useAllOrders();
  const companyOf = useCallback((t) => byOwner[String(t.driver_id)] || byName[String(t.company_name || "").trim().toLowerCase()] || null, [byOwner, byName]);
  const [extras, setExtras] = useState(false);
  const [mode, setMode] = useState("transport"); // tabs «Найти транспорт» | «Найти грузы» switch this form in place
  const cargo = mode === "loads";
  const [openTiles, setOpenTiles] = useState({}); // phone: expanded suggestion cards

  const [form, setForm] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(null); // null = start state (iTruck_Home_Empty_Search), else results (iTruck_Home)
  const [sort, setSort] = useState("relevance");
  const [view, setView] = useState("list");
  const [recent, setRecent] = useState(loadRecent);
  const [shown, setShown] = useState(PAGE);
  const [dialog, setDialog] = useState(null);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);
  const resultsRef = useRef(null);
  const scrollResults = useRef(false);

  const say = useCallback((msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(""), 4200);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // any combination of fields (even none) is a valid search
  const routeLabel = (f) => `${f.origin || "Любой город"} → ${f.destination || "любое направление"}`;

  const start = () => {
    document.querySelector(".cab-home input[name=origin]")?.focus();
    document.querySelector(".cab-home .search-panel")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const rows = useMemo(
    () =>
      applied
        ? sortTrucks(filterTrucks(trucks, applied), sort).filter((t) => !hid.isHidden(t.vehicle_id) && (!applied.verified || companyOf(t)?.verified))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trucks, applied, sort, hid.count, companyOf],
  );
  // «Найти грузы»: published loads, date window and matching TTL not over, never the viewer's own
  const activeLoadList = useMemo(
    () => (allOrders || []).filter((o) => o.status === 1 && notExpired(o) && ttlActive(o, loadTtl) && o.owner !== userId),
    [allOrders, loadTtl, userId],
  );
  const loadsFor = useCallback(
    (f) => filterLoads(activeLoadList, f).filter((o) => !f.verified || byOwner[String(o.owner)]?.verified),
    [activeLoadList, byOwner],
  );
  const loadRows = useMemo(
    () => (cargo && applied ? loadsFor(applied).filter((o) => !hidLoads.isHidden(o.uuid)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cargo, applied, loadsFor, hidLoads.count],
  );
  const cities = useMemo(() => knownCities(trucks), [trucks]);
  const popular = useMemo(() => popularRoutes(trucks), [trucks]);
  // «Найти грузы» tab: the routes most loads are published on
  const popularLoads = useMemo(() => {
    const m = new Map();
    for (const o of allOrders || []) {
      if (o.status !== 1 || !notExpired(o)) continue;
      const a = cityOf(o.from_address);
      const b = cityOf(o.to_address);
      if (a === "—" || b === "—") continue;
      const k = `${a}→${b}`;
      m.set(k, { origin: a, destination: b, n: (m.get(k)?.n || 0) + 1 });
    }
    return [...m.values()].sort((x, y) => y.n - x.n).slice(0, 3);
  }, [allOrders]);
  const routes = mode === "loads" ? popularLoads : popular;

  // search(): show results, scroll to them, toast the count — no field is required
  const search = (e, over) => {
    e?.preventDefault?.();
    const f = { ...form, ...over, origin: form.origin.trim(), destination: form.destination.trim() };
    if (cargo) {
      const n = loadsFor(f).length;
      setApplied(f);
      setRecent(pushRecent(f));
      scrollResults.current = true;
      say(`Показано ${n} ${countWord(n, ["груз", "груза", "грузов"])}`);
      return;
    }
    const n = filterTrucks(trucks, f).filter((t) => !f.verified || companyOf(t)?.verified).length;
    setApplied(f);
    setShown(PAGE);
    setRecent(pushRecent(f));
    scrollResults.current = true;
    say(`Показано ${n} ${countWord(n, ["машина", "машины", "машин"])}`);
  };
  useEffect(() => {
    if (applied && scrollResults.current) {
      scrollResults.current = false;
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [applied]);

  // wireframe reset(): clear the form, back to the start state, 
  const switchMode = (next) => {
    if (next === mode) return start();
    setMode(next);
    setApplied(null);
    setView("list");
    setForm((f) => ({ ...f, verified: false }));
  };

  const reset = () => {
    setForm(EMPTY_FILTERS);
    setExtras(false);
    setApplied(null);
    setView("list");
  };

  // wireframe `route`: fill the form, focus it, ask to press «Найти транспорт» (does not search)
  const route = (r) => {
    setForm((f) => ({
      ...f,
      origin: r.origin || "",
      destination: r.destination || "",
      body: r.body || "Любой",
      capacity: r.capacity || "0",
    }));
    start();
    say("Маршрут выбран. Нажмите «Найти транспорт».");
  };

  const openMap = () => {
    if (applied) setView("map");
    setDialog({ type: "map" });
  };
  const showList = () => {
    if (applied) {
      setView("list");
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else search();
  };

  // ⌘K in the topbar: «Результаты поиска» over the live trucks (iTruck_Home global-search handler)
  useEffect(() => {
    const onSearch = (e) => {
      const q = String(e.detail?.q || "").trim().toLowerCase();
      if (!q) return;
      e.preventDefault();
      setDialog({ type: "results", matches: trucks.filter((t) => truckText(t).includes(q)) });
    };
    window.addEventListener("cab:global-search", onSearch);
    return () => window.removeEventListener("cab:global-search", onSearch);
  }, [trucks]);


  // ---- KPI: numbers from the backend (except the static last tile); trend chip + bars only from a real series ----
  const activeNow = metrics ? metrics.active_available_trucks_now : trucks.filter((t) => t.status === "ACTIVE").length;
  const activeLoads = metrics ? metrics.active_loads : allOrders ? allOrders.filter((o) => o.status === 1 && notExpired(o)).length : null;
  const loadSeries = useMemo(() => dailySeries(allOrders, (o) => o.created_at), [allOrders]);
  const companySeries = useMemo(() => dailySeries(companies, (c) => c.created_at), [companies]);
  const kpis = [
    { tone: 0, d: D.truck, v: loading ? "…" : forbidden && !metrics ? "—" : nf(activeNow), label: "Активных машин", hint: "Сейчас ищут груз" },
    activeLoads != null && { tone: 1, d: D.box, v: nf(activeLoads), label: "Активных грузов", series: loadSeries, hint: "Новых грузов за 7 дней; процент — к предыдущим 7 дням" },
    companies.length > 0 && { tone: 2, d: D.people, v: nf(companies.length), label: "Компании", series: companySeries, hint: "Новых компаний за 7 дней; процент — к предыдущим 7 дням" },
    { tone: 3, d: D.clock, v: "15 мин", label: "до первого отклика" }, // static by decision, not a live metric
  ].filter(Boolean);

  // phone: «Популярные направления» / «Последние поиски» / «Быстрые подсказки» are accordions
  const tile = (k) => ({
    role: "button",
    tabIndex: 0,
    "aria-expanded": !!openTiles[k],
    onClick: () => setOpenTiles((o) => ({ ...o, [k]: !o[k] })),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpenTiles((o) => ({ ...o, [k]: !o[k] }));
      }
    },
  });
  const sentKey = (applied ? rows : trucks).map((t) => (sent.has(t.vehicle_id) ? 1 : 0)).join("");
  // «Найти грузы» tab: before a search the map follows what is typed in the form, after it — the results
  const loadsOnMap = useMemo(() => (!cargo ? [] : applied ? loadRows : loadsFor(form)), [cargo, applied, loadRows, loadsFor, form]);
  const [loadAction, setLoadAction] = useState(null); // map popup → «Откликнуться» / «Детали груза» in the results
  const openLoad = (type, order) => {
    if (!applied) search();
    setLoadAction({ type, order, at: Date.now() });
  };
  const truckPoints = useMemo(
    () =>
      (applied ? rows : trucks).map((t) => ({
        lat: t.lat,
        lng: t.lng,
        city: t.city,
        title: `${t.driver_name} · ${t.mark || ""}`,
        info: truckInfo(t, { sent: sent.has(t.vehicle_id), onRequest: () => setDialog({ type: "request", trucks: [t] }), onDetails: () => setDialog({ type: "details", truck: t }) }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applied, rows, trucks, sentKey],
  );
  const mapPoints = useMemo(
    () =>
      cargo
        ? loadsOnMap.map((o) => ({
            lat: o.location_from?.lat,
            lng: o.location_from?.lng,
            city: cityOf(o.from_address) !== "—" ? o.from_address : null,
            title: `${o.from_address || ""} → ${o.to_address || ""}`,
            to: o.location_to?.lat != null && o.location_to?.lng != null ? { lat: Number(o.location_to.lat), lng: Number(o.location_to.lng) } : null,
            info: loadInfo(o, { onRespond: () => openLoad("respond", o), onDetails: () => openLoad("more", o) }),
          }))
        : truckPoints,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cargo, loadsOnMap, truckPoints],
  );
  const mapTitle = cargo ? "Карта грузов" : applied ? routeLabel(applied) : "Карта транспорта";
  const mapSummary = applied ? (
    <>
      <strong>{routeLabel(applied)}</strong>
      <small>
        {cargo
          ? `Показано ${nf(loadRows.length)} ${countWord(loadRows.length, ["груз", "груза", "грузов"])}`
          : `Показано ${nf(rows.length)} ${countWord(rows.length, ["машина", "машины", "машин"])}`}
      </small>
    </>
  ) : (
    <>
      <strong>Заполните параметры поиска</strong>
      <small>
        Выберите маршрут, чтобы увидеть
        <br />
        {cargo ? "грузы на карте" : "транспорт на карте"}
      </small>
    </>
  );

  return (
    <CabPage name="home">
      <div className="cab-homeempty" style={{ display: "contents" }}>
      <main id="main">
        <section className="hero">
          <div className="intro">
            <h1>
              {HERO[mode].title} <span>за минуты</span>
            </h1>
            <p>{HERO[mode].text}</p>
            <div className="hero-actions">
              <button className="hero-button publish" onClick={() => navigate("/order")}>
                <Icon d={D.box} />
                <span>
                  <strong>Опубликовать груз</strong>
                  <small>
                    Получите предложения
                    <br />
                    от перевозчиков →
                  </small>
                </span>
              </button>
              <button className="hero-button add-truck" onClick={() => navigate("/my-fleet/add")}>
                <Icon d={D.truck} />
                <span>
                  <strong>Добавить свободную машину</strong>
                  <small>Ваша машина ищет груз →</small>
                </span>
              </button>
            </div>
          </div>
          <div className="metrics">
            {kpis.map((k, i) => (
              <article key={i} className={`kpi kpi-${k.tone}`}>
                <span className="metric-icon">
                  <Icon d={k.d} />
                </span>
                <b>{k.v}</b>
                <span className="metric-label">{k.label}</span>
                {trendOf(k.series) && (
                  <span className={`change${trendOf(k.series).down ? " down" : ""}`} title={k.hint}>
                    {trendOf(k.series).text}
                  </span>
                )}
                <TileBars />
              </article>
            ))}
          </div>
        </section>

        <section className="search-layout">
          <div className="search-panel">
            <div className="search-head">
              <div className="search-tabs" role="tablist" aria-label="Тип поиска">
                <button className={`search-tab${cargo ? "" : " active"}`} role="tab" aria-selected={!cargo} onClick={() => switchMode("transport")}>
                  <Icon d={D.truck} />
                  Найти транспорт
                </button>
                <button className={`search-tab${cargo ? " active" : ""}`} role="tab" aria-selected={cargo} onClick={() => switchMode("loads")}>
                  <Icon d={D.box} />
                  Найти грузы
                </button>
              </div>
              <div className="views" role="group" aria-label="Вид результатов">
                <button type="button" className={view === "map" ? "active" : ""} onClick={openMap}>
                  <Icon d="m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2z M9 3v16 M15 5v16" />
                  На карте
                </button>
                <button type="button" className={view === "list" ? "active" : ""} onClick={showList}>
                  <Icon d="M7 6h15 M7 12h15 M7 18h15 M2 6h1 M2 12h1 M2 18h1" />
                  Списком
                </button>
              </div>
            </div>
            <form id="search-form" onSubmit={search}>
              <SearchFields form={form} setForm={setForm} kind={cargo ? "loads" : "trucks"} layout="home" extras={extras} />
              <div className="search-footer">
                <button type="button" className="extra-button" aria-expanded={extras} onClick={() => setExtras((v) => !v)}>
                  Дополнительные фильтры
                  <Icon d={D.chevron} />
                </button>
                <div className="search-buttons">
                  <div className="views mobile-views" role="group" aria-label="Вид результатов">
                    <button type="button" onClick={openMap}>
                      <Icon d="m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2z M9 3v16 M15 5v16" />
                      На карте
                    </button>
                    <button type="button" className="active" onClick={showList}>
                      <Icon d="M7 6h15 M7 12h15 M7 18h15 M2 6h1 M2 12h1 M2 18h1" />
                      Списком
                    </button>
                  </div>
                  <button type="button" className="button reset" onClick={reset}>
                    Сбросить
                  </button>
                  <button type="submit" className="button primary search-submit">
                    <Icon d={D.search} />
                    {cargo ? "Найти грузы" : "Найти транспорт"}
                  </button>
                </div>
              </div>
            </form>
          </div>

          <MapPanel
            ariaLabel="Карта транспорта"
            points={mapPoints}
            summary={mapSummary}
            icon={D.pin}
            expandLabel="Показать все на карте"
            onExpand={openMap}
            onCenter={applied ? () => say("Показан весь маршрут") : undefined}
          />
        </section>

        {forbidden && (
          <div className="live-note" role="alert" style={{ padding: "0 24px" }}>
            <strong>{SEARCH_FORBIDDEN_TEXT}</strong>
          </div>
        )}
        {error && !forbidden && (
          <div className="live-note" style={{ padding: "0 24px" }}>
            Не удалось загрузить машины.{" "}
            <button type="button" style={{ color: "#0063ff" }} onClick={reload}>
              Повторить
            </button>
          </div>
        )}

        {applied === null ? (
          <section className="search-start" id="search-start">
            <div className="start-intro">
              <h2>{cargo ? "Начните поиск груза" : "Начните поиск транспорта"}</h2>
              <p>
                {cargo ? "Укажите маршрут и параметры машины," : "Укажите маршрут и параметры перевозки,"}
                <br />
                {cargo ? "чтобы найти подходящие грузы." : "чтобы система нашла подходящие машины."}
              </p>
              <button className="button primary" type="button" onClick={() => search()}>
                <Icon d={D.search} />
                Начать поиск
              </button>
              <img className="start-art" src={startArt} alt="" aria-hidden="true" />
            </div>

            {routes.length > 0 && (
              <div className={`suggestion popular${openTiles.popular ? " expanded" : ""}`}>
                <header {...tile("popular")}>
                  <span className="tile">☆</span>
                  <div>
                    <h3>Популярные направления</h3>
                    <p>
                      Самые востребованные маршруты
                      <br />
                      {cargo ? "среди грузоотправителей." : "среди перевозчиков."}
                    </p>
                  </div>
                  <span className="expand-chevron">›</span>
                </header>
                <div className="suggestion-body">
                  {routes.map((r) => (
                    <button key={`${r.origin}-${r.destination}`} onClick={() => route(r)}>
                      <Icon d={D.pin} />
                      <span>
                        <strong>
                          {r.origin} → {r.destination}
                        </strong>
                        <small>
                          {r.n} {countWord(r.n, cargo ? ["груз", "груза", "грузов"] : ["машина", "машины", "машин"])}
                        </small>
                      </span>
                      <i>›</i>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={`suggestion recent${openTiles.recent ? " expanded" : ""}`}>
              <header {...tile("recent")}>
                <span className="tile">
                  <Icon d={D.clock} />
                </span>
                <div>
                  <h3>Последние поиски</h3>
                  <p>
                    Быстрый доступ к вашим
                    <br />
                    недавним запросам.
                  </p>
                </div>
                <span className="expand-chevron">›</span>
              </header>
              <div className="suggestion-body" id="recent-list">
                {recent.length === 0 && <div className="live-note">Пока нет поисков</div>}
                {recent.map((r, i) => (
                  <button key={i} onClick={() => route(r)}>
                    <span>
                      <strong>
                        {r.origin || "Любой город"} → {r.destination || "любое направление"}
                      </strong>
                      <small>{describeFilters(r)}</small>
                    </span>
                    <i>Повторить</i>
                  </button>
                ))}
              </div>
            </div>

            <div className={`suggestion tips${openTiles.tips ? " expanded" : ""}`}>
              <header {...tile("tips")}>
                <span className="tile">♧</span>
                <div>
                  <h3>Быстрые подсказки</h3>
                  <p>
                    Полезные функции для эффективного
                    <br />
                    {cargo ? "поиска грузов." : "поиска транспорта."}
                  </p>
                </div>
                <span className="expand-chevron">›</span>
              </header>
              <div className="suggestion-body">
                <button onClick={() => setDialog({ type: "nearby" })}>
                  <span className="tip-icon">
                    <Icon d={D.locate} />
                  </span>
                  <span>
                    <strong>{cargo ? "Найти грузы рядом" : "Найти машины рядом"}</strong>
                    <small>{cargo ? "Покажем грузы в вашем регионе" : "Покажем транспорт в вашем регионе"}</small>
                  </span>
                  <i>›</i>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExtras(true);
                    if (cargo) setForm((f) => ({ ...f, verified: true }));
                    else setTimeout(() => document.querySelector(".cab-home select[name=volume]")?.focus(), 0);
                  }}
                >
                  <span className="tip-icon">
                    <Icon d={D.check} />
                  </span>
                  <span>
                    <strong>{cargo ? "Проверенные грузоотправители" : "Указать объём кузова"}</strong>
                    <small>{cargo ? "Только надёжные компании" : "Подбор по вместимости машины"}</small>
                  </span>
                  <i>›</i>
                </button>
                <button onClick={openMap}>
                  <span className="tip-icon">
                    <Icon d="m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2z M9 3v16 M15 5v16" />
                  </span>
                  <span>
                    <strong>{cargo ? "Посмотреть грузы на карте" : "Посмотреть транспорт на карте"}</strong>
                    <small>Удобный просмотр маршрута</small>
                  </span>
                  <i>›</i>
                </button>
              </div>
            </div>
          </section>
        ) : (
          cargo ? (
          <HomeLoadResults
            ref={resultsRef}
            rows={loadRows}
            caption={routeLabel(applied)}
            byOwner={byOwner}
            hid={hidLoads}
            action={loadAction}
            onReset={reset}
            say={say}
          />
          ) : (
          <section className="results" ref={resultsRef}>
            <div className="results-heading">
              <div>
                <h2>
                  Найдено <span id="result-count">{rows.length}</span> {countWord(rows.length)}
                </h2>
                <p>{routeLabel(applied)}</p>
              {hid.count ? (
                <button className="hidden-rows" type="button" onClick={hid.showAll}>
                  Показать скрытые ({hid.count})
                </button>
              ) : null}
              </div>
              <label className="sort">
                <span>Сортировка:</span>
                <select id="sort" aria-label="Сортировка" value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="relevance">По релевантности</option>
                  <option value="rate">По ставке</option>
                  <option value="distance">По расстоянию</option>
                </select>
                <Icon d={D.chevron} />
              </label>
            </div>
            <div className="table-container">
              <table>
                <colgroup>
                  <col style={{ width: "19%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "17%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Водитель / Компания</th>
                    <th>Машина</th>
                    <th>Где сейчас</th>
                    <th>Куда ищет</th>
                    <th>Свободен</th>
                    <th>Ставка</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, shown).map((t) => {
                    const ds = destCities(t);
                    const isSent = sent.has(t.vehicle_id);
                    return (
                      <tr key={rowKey(t)}>
                        <td data-label="Водитель / Компания">
                          <div className="carrier">
                            <CompanyLogo src={companyOf(t)?.profile_images} />
                            <div>
                              <strong>
                                <span className="name" title={t.company_name || t.driver_name || ""}>{t.company_name || t.driver_name || "—"}</span>
                                {companyOf(t)?.verified ? (
                                  <span className="verified" role="img" aria-label="Проверен" title="Проверенная компания">
                                    ✓
                                  </span>
                                ) : null}
                              </strong>
                              <small>{t.company_name && t.driver_name ? t.driver_name : t.status === "BUSY" ? "В рейсе" : "Свободен"}</small>
                            </div>
                          </div>
                        </td>
                        <td data-label="Машина">
                          <div className="vehicle">
                            <VehiclePhoto label={t.mark || "Машина"} category={t.category_name} />
                            <div>
                              <strong>{t.mark || "—"}</strong>
                              <small>
                                {[t.body_type_name, t.max_load_weight != null ? `${t.max_load_weight} т` : null].filter(Boolean).join(" • ")}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td data-label="Где сейчас">
                          <strong>{t.city || "—"}</strong>
                          <small>{t._dist != null ? `${t._dist} км от «Откуда»` : fmtAgo(t.confirmed_minutes_ago)}</small>
                        </td>
                        <td data-label="Куда ищет">
                          <span>{ds[0] || "Любое направление"}</span>
                          <small>{ds.slice(1).join(", ")}</small>
                        </td>
                        <td data-label="Свободен">
                          <span>{fmtFree(t)}</span>
                          <small></small>
                        </td>
                        <td data-label="Ставка">
                          <span>{t.min_price != null ? <>от <b>{`${Number(t.min_price).toLocaleString("ru-RU")} ${t.currency || ""}`.trim()}</b></> : "—"}</span>
                          <small></small>
                        </td>
                        <td data-label="Действия">
                          <div className="row-actions">
                            <button
                              className="button primary request"
                              disabled={isSent}
                              onClick={() => setDialog({ type: "request", trucks: [t] })}
                            >
                              {isSent ? "Заявка отправлена" : "Отправить заявку"}
                            </button>
                            <button className="button more" aria-haspopup="menu" aria-label={`Действия с ${t.driver_name}`} title="Действия с машиной" onClick={(e) => rowMenu.openMenu(e, t)}>
                              ···
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length > shown && (
                <div className="results-more">
                  <button className="button" onClick={() => setShown((n) => n + PAGE)}>
                    Показать ещё
                  </button>
                </div>
              )}
              {!forbidden && rows.length === 0 && (
                <div className="empty">
                  <h3>Подходящих машин не найдено</h3>
                  <p>Измените параметры поиска или сбросьте фильтры.</p>
                  <button className="button" onClick={reset}>
                    Сбросить фильтры
                  </button>
                </div>
              )}
            </div>
          </section>
          )
        )}
      </main>
      </div>

      <MobileNav />

      {rowMenu.menu && (
        <RowMenu
          anchor={rowMenu.menu.anchor}
          items={[
            { label: "Открыть детали", onClick: () => setDialog({ type: "details", truck: rowMenu.menu.payload }) },
            { label: "Скрыть из результатов", onClick: () => hid.hide(rowMenu.menu.payload.vehicle_id) },
          ]}
          onClose={rowMenu.closeMenu}
        />
      )}
      {dialog?.type === "request" && (
        <TruckRequestPanel
          trucks={dialog.trucks}
          onClose={() => setDialog(null)}
          onSent={(ids) => {
            sent.add(ids, null);
            say("Заявка отправлена");
          }}
        />
      )}
      {dialog?.type === "details" && (
        <TruckDetailsPanel
          truck={dialog.truck}
          sent={sent.has(dialog.truck.vehicle_id)}
          onRequest={() => setDialog({ type: "request", trucks: [dialog.truck] })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "results" && <TruckResultsDialog matches={dialog.matches} onClose={() => setDialog(null)} />}
      {dialog?.type === "map" && (
        <Modal title={mapTitle} wide full onClose={() => setDialog(null)}>
          <MapPanel ariaLabel="Карта транспорта" points={mapPoints} summary={mapSummary} icon={D.pin} />
        </Modal>
      )}
      {dialog?.type === "nearby" && (
        <NearbyDialog
          initial={cities[0] || ""}
          onClose={() => setDialog(null)}
          onPick={(city) => {
            setForm((f) => ({ ...f, origin: city }));
            setDialog(null);
            start();
            say("Город выбран. Укажите направление.");
          }}
        />
      )}
      <div id="toast" role="status" aria-live="polite" className={toast ? "visible" : ""}>
        {toast}
      </div>
      <datalist id="city-options">
        {cities.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </CabPage>
  );
}

/** Tip «Найти машины рядом»: dialog «Машины рядом» (Ваш город → «Выбрать город»). */
function NearbyDialog({ initial, onClose, onPick }) {
  const [city, setCity] = useState(initial);
  return (
    <Modal title="Машины рядом" onClose={onClose}>
      <form
        id="demo-form"
        onSubmit={(e) => {
          e.preventDefault();
          onPick(city.trim());
        }}
      >
        <Field label="Ваш город" name="city" value={city} onChange={(e) => setCity(e.target.value)} list="city-options" />
        <div className="actions">
          <button className="button" type="button" onClick={onClose}>
            Отмена
          </button>
          <button className="button primary" type="submit">
            Выбрать город
          </button>
        </div>
      </form>
    </Modal>
  );
}
